<script setup>
    import { ref, reactive, onBeforeUpdate, onMounted, watch } from 'vue';
    import Popper from 'vue3-popper';

    const props = defineProps({
        campaignType: [String, null],
        webshopUrl: [String, null],
        selectedCities: Array,
        availableCities: Array,
        availableTypes: Array,
        editMode: Boolean,
        errors: Object,
    });

    const emits = defineEmits(['update:campaignType', 'update:webshopUrl', 'update:selectedCities']);

    const refs = ref([]);

    onBeforeUpdate(() => {
        refs.value = [];
    });

    const typesInfo = reactive({
        'webshop': {
            title: 'E-Handel',
            desc: 'Välj denna när medlemmen ska utnyttja kampanjen digitalt.',
        },
        'local': {
            title: 'Fysisk / Lokal',
            desc: 'Denna ska du använda när medlemmen ska använda kampanjen fysiskt. T.ex. genom att visa medlemskortet & koden i kassan.',
        },
    });

    const types = ref(props.availableTypes.map((t) => {
        return { ...t, ...typesInfo[t.code] };
    }));

    const selectedType = ref(props.campaignType);
    const _webshopUrl = ref(props.webshopUrl);
    const _selectedCities = ref(props.selectedCities);

    function selectType(type) {

        if (selectedType.value === type.code || props.editMode) {
            return;
        }

        refs.value.forEach((ref) => {
            ref.hide();
        });

        selectedType.value = type.code;
        emits('update:campaignType', selectedType.value);
    }

    function webshopUrlUpdated(e) {
        emits('update:webshopUrl', _webshopUrl.value);
    }

    watch(_selectedCities, function () {
        emits('update:selectedCities', _selectedCities.value);
    });
</script>

<template>
    <div :class="{ 'flex flex-col md:flex-row mb-6': editMode }">
        <div class="mb-4 md:mb-0 md:mx-2" :class="{ 'md:w-1/3': editMode, 'mb-4 w-80 flex-none': !editMode }">
            <h2 class="text-2xl mb-2 text-red font-averta">Välj typ av kampanj</h2>
            <p class="text-sm text-gray font-avertaLight">Här väljer du vilken typ av kampanj du vill skapa. Ska affären göras digitalt, eller fysiskt på plats?</p>
        </div>

        <div class="md:mx-2 rounded-md" :class="{ 'md:w-2/3': editMode }">
            <div class="flex flex-col rounded-md border-2 border-blue-800 border-opacity-20">
                <div
                    v-for="type in types"
                    @click="selectType(type)"
                    class="w-full flex px-4 py-4 cursor-pointer relative text-gray font-avertaRegular"
                    :class="{
                        'bg-blue': selectedType === type.code,
                        'text-gray': selectedType !== type.code,
                        'cursor-not-allowed': (editMode && selectedType !== type.code)
                    }">

                    <svg v-if="selectedType === type.code" xmlns="http://www.w3.org/2000/svg" class="absolute text-green-800 top-3 right-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>

                    <div v-if="selectedType !== type.code" class="h-5 w-5 border-2 border-green rounded-full absolute top-3 right-3"></div>

                    <svg v-if="type.code === 'webshop'" xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 flex-none text-gray" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                    </svg>

                    <svg v-if="type.code === 'local'" xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 flex-none text-gray" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>

                    <div class="ml-4">
                        <div class="text-xl font-averta text-gray">{{ type.title }}</div>

                        <div class="text-sm"><expandable-text :ref="el => { refs.push(el) }" :max-length="100" class="font-avertaLight text-gray">{{ type.desc }}</expandable-text></div>

                        <div v-if="type.code === 'local' && selectedType === type.code && availableCities.length >= 2" class="mt-2 space-x-2">
                            <label v-for="availableCity in availableCities" class="mt-1 inline-flex items-center text-sm">
                                <input
                                    v-model="_selectedCities"
                                    :value="availableCity.id"
                                    class="rounded-full text-green-800"
                                    type="checkbox"><span class="ml-1 text-gray font-averta">{{ availableCity.name }}</span>
                            </label>
                        </div>

                        <div v-if="type.code === 'webshop' && selectedType === type.code" class="mt-2">
                            <label class="font-avertaBold text-sm text-gray">Url/länk till kampanjsida</label>

                            <Popper :hover="true" style="display: inline-flex;">
                                <icon name="info" class="ml-2"></icon>

                                <template #content>
                                    <div class="popper-client-admin">När en medlem klickar sig vidare från kampanjen, så är det till den url/länk ni anger här som medlemmen kommer till.</div>
                                </template>
                            </Popper>

                            <input
                                v-model="_webshopUrl"
                                @input="webshopUrlUpdated"
                                :class="{ 'border-red': errors.url }"
                                :error="errors.url"
                                class="mt-2 rounded-md ring-0 outline-none border-2 border-blue focus:border-blue-600 py-2 px-4 w-full font-avertaLight text-sm"
                                type="text"
                                name="url"
                                placeholder="*Webbsida" />

                            <div v-if="errors.url" class="text-red mt-2 text-sm">
                                {{ errors.url }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
